// src/pages/ViewReport/ViewReportPage.js

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { doc, setDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import PasswordWall from './PasswordWall';
import AnalysisReportView from './AnalysisReportView';
import GenerationReportView from './GenerationReportView';
import {
  fetchReport,
  getErrorMessage,
  extractAnalysisFromReport,
  extractMetadataFromReport,
  extractImagesFromReport,
} from '../../utils/reportApi';
import {
  initializeSession,
  trackEvent,
  linkViewReportToSession,
  trackViewReportPageView,
  trackViewReportApiCall,
  trackViewReportContactSubmission,
  trackViewReportContactButtonClick,
} from '../../utils/Tracking';
import {
  createInitialViewReportState,
  applyViewReportFieldUpdates,
  markViewReportFieldsAsSynced,
  recordViewReportSyncError,
} from '../../utils/ViewReport/viewReportState';
import {
  buildViewReportSyncPayload,
  computeViewReportLocalFingerprint,
} from '../../utils/ViewReport/viewReportSync';
import { extractUtmParameters } from '../../utils/ViewReport/viewReportSession';
import { trackMetaLead } from '../../utils/metaPixel';
import { trackTikTokContact } from '../../utils/tikTokPixel';

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------

const SYNC_INTERVAL_MS = 2000;
const SESSION_STORAGE_KEY = 'mm_view_report_state_v1';
const APPEND_ONLY_FIELDS = new Set([
  'sessionIds',
  'tabsViewed',
  'fetchReportAttempts',
  'contactSubmissions',
  'welcomeEmailAttempts',
  'contactButtonClicks',
  'userNavLog',
  'pageViewDurations',
  'errors',
]);

// ---------------------------------------------------------------------------
// PERSISTENCE HELPERS
// ---------------------------------------------------------------------------

function loadPersistedViewReportState(requestId) {
  if (typeof window === 'undefined') {
    return createInitialViewReportState({ requestId });
  }
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return createInitialViewReportState({ requestId });
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return createInitialViewReportState({ requestId });
    }
    // Only restore if it's for the same requestId
    if (parsed.session?.requestId !== requestId) {
      return createInitialViewReportState({ requestId });
    }
    const base = createInitialViewReportState({
      requestId,
      createdAt: parsed.session?.createdAt,
      sessionId: parsed.session?.id,
    });
    return {
      ...base,
      session: { ...base.session, ...(parsed.session || {}) },
      fields: { ...base.fields, ...(parsed.fields || {}) },
      latestValues: { ...base.latestValues, ...(parsed.latestValues || {}) },
      dirtyFields: parsed.dirtyFields || {},
      hasPendingChanges: Boolean(parsed.hasPendingChanges),
      sync: { ...base.sync, ...(parsed.sync || {}) },
    };
  } catch (error) {
    console.error('[ViewReportStorage] Failed to load state from sessionStorage', error);
    return createInitialViewReportState({ requestId });
  }
}

function persistViewReportState(state) {
  if (typeof window === 'undefined' || !state) return;
  try {
    const payload = {
      session: state.session,
      fields: state.fields,
      latestValues: state.latestValues,
      dirtyFields: state.dirtyFields || {},
      hasPendingChanges: Boolean(state.hasPendingChanges),
      sync: state.sync,
    };
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('[ViewReportStorage] Failed to persist state to sessionStorage', error);
  }
}

/**
 * Loading Screen Component
 */
function LoadingScreen({ message = 'Loading your report...' }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
          <svg 
            className="animate-spin h-12 w-12 text-white" 
            viewBox="0 0 24 24" 
            fill="none"
          >
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4" 
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
            />
          </svg>
        </div>
        <p className="text-white text-lg">{message}</p>
      </div>
    </div>
  );
}

/**
 * Error Screen Component
 */
function ErrorScreen({ title, message, onRetry }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-4">
          <svg 
            className="w-8 h-8 text-red-400" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
        </div>
        <h2 className="text-white text-xl font-bold mb-2">{title}</h2>
        <p className="text-gray-400 mb-6">{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-6 py-2.5 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-colors"
          >
            Try Again
          </button>
        )}
        <div className="mt-4">
          <a 
            href="/" 
            className="text-gray-400 hover:text-white transition-colors text-sm"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}

// TabSwitcher moved to individual view components' top banners

/**
 * ViewReportPage - Main page for viewing saved reports.
 * 
 * URL Structure:
 * /view-report/:requestId
 * /view-report/:requestId?tab=generation
 * 
 * Flow:
 * 1. Show password wall
 * 2. On password submit, fetch report from API
 * 3. Display analysis or generation view based on tab
 * 
 * Firestore Integration:
 * - Creates view_report document in recommendations_form_data/{requestId}/view_report/{viewReportId}
 * - Syncs state every 2 seconds
 * - Links to tracking session via view_report_id
 */
export default function ViewReportPage() {
  const { requestId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // State
  const [pageState, setPageState] = useState('password'); // 'password' | 'loading' | 'ready' | 'error'
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [errorInfo, setErrorInfo] = useState({ title: '', message: '' });

  // View Report State Management
  const [viewReportState, setViewReportState] = useState(() => 
    loadPersistedViewReportState(requestId)
  );
  const latestViewReportValues = viewReportState.latestValues;

  // Sync refs
  const pendingSyncRef = useRef(null);
  const lastSyncedFingerprintRef = useRef(null);
  const isSyncingRef = useRef(false);
  const lastSyncStartedAtRef = useRef(0);
  const isMountedRef = useRef(true);
  const hasInitializedTrackingRef = useRef(false);
  const tabsViewedRef = useRef(new Set());

  // Cleanup on unmount
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  // Get current tab from URL
  const currentTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return tab === 'generation' ? 'generation' : 'analysis';
  }, [searchParams]);

  // Handle tab change
  const handleTabChange = useCallback((newTab) => {
    if (newTab === 'analysis') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', newTab);
    }
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  // Handle persistent field updates
  const persistFieldUpdates = useCallback((updates = {}, options = {}) => {
    if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
      return;
    }
    setViewReportState((prev) => applyViewReportFieldUpdates(prev, updates, options));
  }, []);

  // Persist state to sessionStorage
  useEffect(() => {
    persistViewReportState(viewReportState);
  }, [viewReportState]);

  // ---------------------------------------------------------------------------
  // TRACKING INITIALIZATION
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (hasInitializedTrackingRef.current) return;
    hasInitializedTrackingRef.current = true;

    (async () => {
      try {
        // Initialize tracking session
        await initializeSession();
        const trackingSessionId = typeof window !== 'undefined' && window.sessionStorage
          ? window.sessionStorage.getItem('current_session_id')
          : null;

        // Record tracking session ID
        if (trackingSessionId) {
          persistFieldUpdates({ sessionIds: [trackingSessionId] }, { source: 'system' });
        }

        // Link view_report to tracking session
        if (viewReportState?.session?.id) {
          await linkViewReportToSession(viewReportState.session.id, requestId);
        }

        // Record landing context
        persistFieldUpdates({
          reportRequestId: requestId,
          landingUrl: window.location.href,
          referrer: document.referrer || 'direct',
          utmParameters: extractUtmParameters(window.location.search),
        }, { source: 'system' });

        // Track page view event
        await trackViewReportPageView({
          viewReportId: viewReportState?.session?.id,
          reportRequestId: requestId,
          pageName: 'password_wall',
        });
      } catch (e) {
        console.error('[ViewReport Tracking] init error:', e);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // TAB/PAGE VIEW TRACKING
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (pageState !== 'ready') return;
    
    // Track tabs viewed (append-only)
    if (!tabsViewedRef.current.has(currentTab)) {
      tabsViewedRef.current.add(currentTab);
      persistFieldUpdates({ tabsViewed: [currentTab] }, { source: 'navigation' });
      
      // Track navigation log
      persistFieldUpdates({ userNavLog: [currentTab] }, { source: 'navigation' });
      
      // Track page view event
      void trackViewReportPageView({
        viewReportId: viewReportState?.session?.id,
        reportRequestId: requestId,
        pageName: 'report_view',
        tabName: currentTab,
      });
    }
  }, [pageState, currentTab, requestId, viewReportState?.session?.id, persistFieldUpdates]);

  // ---------------------------------------------------------------------------
  // FIRESTORE SYNC
  // ---------------------------------------------------------------------------

  const localFingerprint = useMemo(() => computeViewReportLocalFingerprint(viewReportState), [viewReportState]);
  const latestSyncPayload = useMemo(() => buildViewReportSyncPayload(viewReportState), [viewReportState]);

  const attemptSync = useCallback(async () => {
    const pending = pendingSyncRef.current;
    if (!pending || !pending.fingerprint) return;
    if (pending.fingerprint === lastSyncedFingerprintRef.current) return;
    if (isSyncingRef.current) return;

    const now = Date.now();
    if (now - lastSyncStartedAtRef.current < SYNC_INTERVAL_MS) return;

    const payload = pending.payload;
    if (!payload || !payload.session?.id) return;

    const dirtyFields = Array.isArray(payload.dirtyFields) ? payload.dirtyFields : [];
    if (dirtyFields.length === 0) {
      lastSyncedFingerprintRef.current = pending.fingerprint;
      return;
    }

    const updateFields = {};
    dirtyFields.forEach((fieldKey) => {
      if (Object.prototype.hasOwnProperty.call(payload.fields, fieldKey)) {
        updateFields[fieldKey] = payload.fields[fieldKey];
      }
    });

    if (Object.keys(updateFields).length === 0) {
      lastSyncedFingerprintRef.current = pending.fingerprint;
      if (isMountedRef.current) {
        setViewReportState((prev) =>
          markViewReportFieldsAsSynced(prev, dirtyFields, {
            lastSyncedAt: payload.generatedAt,
            firestoreDocumentId: payload.session.id,
          })
        );
      }
      return;
    }

    lastSyncStartedAtRef.current = now;

    // Document path: recommendations_form_data/{requestId}/view_report/{viewReportId}
    const docRef = doc(
      db, 
      'recommendations_form_data', 
      payload.session.requestId || 'unknown', 
      'view_report', 
      payload.session.id
    );

    const firestorePatch = {
      sessionMetadata: {
        id: payload.session.id,
        requestId: payload.session.requestId || null,
        createdAt: payload.session.createdAt ?? payload.generatedAt,
        lastUpdatedAt: payload.session.lastUpdatedAt ?? payload.generatedAt,
      },
      lastSyncedAt: serverTimestamp(),
      updates: arrayUnion({
        timestamp: payload.generatedAt,
        fields: updateFields,
      }),
    };

    // Build latestSnapshot with proper handling of append-only fields
    Object.entries(updateFields).forEach(([fieldKey, value]) => {
      if (typeof value === 'undefined') return;
      if (APPEND_ONLY_FIELDS.has(fieldKey) && Array.isArray(value) && value.length > 0) {
        firestorePatch[`latestSnapshot.${fieldKey}`] = arrayUnion(...value);
      } else {
        firestorePatch[`latestSnapshot.${fieldKey}`] = value;
      }
    });
    firestorePatch['latestSnapshot.lastUpdatedAt'] = serverTimestamp();

    isSyncingRef.current = true;
    try {
      await setDoc(docRef, firestorePatch, { merge: true });
      lastSyncedFingerprintRef.current = pending.fingerprint;
      if (isMountedRef.current) {
        setViewReportState((prev) =>
          markViewReportFieldsAsSynced(prev, dirtyFields, {
            lastSyncedAt: payload.generatedAt,
            firestoreDocumentId: payload.session.id,
          })
        );
      }
    } catch (error) {
      console.error('[ViewReportSync] Failed to upload view report data to Firestore', error);
      if (isMountedRef.current) {
        setViewReportState((prev) =>
          recordViewReportSyncError(prev, error, { lastSyncAttemptAt: new Date().toISOString() })
        );
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, []);

  // Trigger sync when state changes
  useEffect(() => {
    if (viewReportState.hasPendingChanges && localFingerprint) {
      pendingSyncRef.current = { fingerprint: localFingerprint, payload: latestSyncPayload };
      void attemptSync();
    } else {
      pendingSyncRef.current = null;
    }
  }, [attemptSync, localFingerprint, latestSyncPayload, viewReportState.hasPendingChanges]);

  // Periodic sync
  useEffect(() => {
    const syncTimer = setInterval(() => {
      void attemptSync();
    }, SYNC_INTERVAL_MS);
    return () => clearInterval(syncTimer);
  }, [attemptSync]);

  // Extract data from report
  const analysis = useMemo(() => {
    if (!reportData) return null;
    return extractAnalysisFromReport(reportData);
  }, [reportData]);

  const metadata = useMemo(() => {
    if (!reportData) return {};
    return extractMetadataFromReport(reportData);
  }, [reportData]);

  const images = useMemo(() => {
    if (!reportData) return { originalImageUrl: null, generatedImageUrl: null };
    return extractImagesFromReport(reportData);
  }, [reportData]);

  // Check what data is available
  const hasAnalysis = !!analysis;
  const hasGeneration = !!images.generatedImageUrl;

  // Get score values from metadata (with fallbacks)
  const candidateScorePercent = metadata?.candidateScorePercent ?? 72;
  const expectedSatisfactionPercent = metadata?.expectedSatisfactionPercent ?? 92;

  // Handle password submission
  const handlePasswordSubmit = useCallback(async (enteredPassword) => {
    setIsVerifying(true);
    setPasswordError(null);
    
    const attemptTimestamp = new Date().toISOString();
    
    try {
      const data = await fetchReport({
        requestId,
        password: enteredPassword,
        reportType: 'analysis', // Always fetch analysis first, it includes linkedGeneration
        includeLinked: true,
      });
      
      setPassword(enteredPassword);
      setReportData(data);
      setPageState('ready');
      
      // Record successful API call
      persistFieldUpdates({
        fetchReportAttempts: [{
          timestamp: attemptTimestamp,
          reportType: 'analysis',
          success: true,
          includeLinked: true,
        }],
        reportMetadataReceived: extractMetadataFromReport(data),
      }, { source: 'api' });
      
      // Track API call event
      void trackViewReportApiCall({
        viewReportId: viewReportState?.session?.id,
        reportRequestId: requestId,
        reportType: 'analysis',
        success: true,
      });
      
    } catch (error) {
      console.error('[ViewReportPage] Fetch error:', error);
      
      // Record failed API call
      persistFieldUpdates({
        fetchReportAttempts: [{
          timestamp: attemptTimestamp,
          reportType: 'analysis',
          success: false,
          error: error.message || 'Unknown error',
          statusCode: error.status || null,
        }],
      }, { source: 'api' });
      
      // Track API call event
      void trackViewReportApiCall({
        viewReportId: viewReportState?.session?.id,
        reportRequestId: requestId,
        reportType: 'analysis',
        success: false,
        error: error.message,
        statusCode: error.status,
      });
      
      if (error.status === 403) {
        // Wrong password - stay on password wall
        setPasswordError(getErrorMessage(error));
      } else if (error.status === 404) {
        // Report not found - show error screen
        setErrorInfo({
          title: 'Report Not Found',
          message: getErrorMessage(error),
        });
        setPageState('error');
      } else {
        // Other errors - show error screen
        setErrorInfo({
          title: 'Something Went Wrong',
          message: getErrorMessage(error),
        });
        setPageState('error');
      }
    } finally {
      setIsVerifying(false);
    }
  }, [requestId, persistFieldUpdates, viewReportState?.session?.id]);

  // ---------------------------------------------------------------------------
  // CONTACT FORM HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Handle phone number submission from contact form
   */
  const handlePhoneSubmission = useCallback((data) => {
    const { phoneNumber, dialCode, countryCode } = data;
    const timestamp = new Date().toISOString();
    
    // Update latest contact info
    persistFieldUpdates({
      primaryContactPhoneNumber: phoneNumber,
      primaryContactDialCode: dialCode,
      primaryContactPhoneRegion: countryCode,
      preferredFollowUpChannel: 'phone',
      contactSubmissions: [{
        timestamp,
        type: 'phone',
        phoneNumber,
        dialCode,
        countryCode,
        source: 'view_report',
      }],
    }, { source: 'user' });
    
    // Track event
    void trackViewReportContactSubmission({
      viewReportId: viewReportState?.session?.id,
      reportRequestId: requestId,
      contactType: 'phone',
      phoneNumber,
      dialCode,
      phoneRegion: countryCode,
      source: 'view_report',
    });
  }, [persistFieldUpdates, viewReportState?.session?.id, requestId]);

  /**
   * Handle email submission from contact form
   */
  const handleEmailSubmission = useCallback((data) => {
    const { email } = data;
    const timestamp = new Date().toISOString();
    
    // Update latest contact info
    persistFieldUpdates({
      primaryContactEmailAddress: email,
      preferredFollowUpChannel: 'email',
      contactSubmissions: [{
        timestamp,
        type: 'email',
        email,
        source: 'view_report',
      }],
    }, { source: 'user' });
    
    // Track event
    void trackViewReportContactSubmission({
      viewReportId: viewReportState?.session?.id,
      reportRequestId: requestId,
      contactType: 'email',
      emailAddress: email,
      source: 'view_report',
    });
  }, [persistFieldUpdates, viewReportState?.session?.id, requestId]);

  /**
   * Handle contact button click (WhatsApp, etc.)
   */
  const handleContactButtonClick = useCallback((buttonType, href, pageName) => {
    const timestamp = new Date().toISOString();
    
    // Record the click
    persistFieldUpdates({
      contactButtonClicks: [{
        timestamp,
        buttonType,
        href,
        pageName,
      }],
    }, { source: 'interaction' });
    
    // Track event
    void trackViewReportContactButtonClick({
      viewReportId: viewReportState?.session?.id,
      reportRequestId: requestId,
      buttonType,
      href,
      pageName,
    });
    // Track TikTok Pixel Contact event
    trackMetaLead({ content_name: 'view_report_contact', content_category: 'view_report_contact' });
    trackTikTokContact('view_report_contact');
  }, [persistFieldUpdates, viewReportState?.session?.id, requestId]);

  // Handle retry from error screen
  const handleRetry = useCallback(() => {
    setPageState('password');
    setPasswordError(null);
    setReportData(null);
  }, []);

  // Validate requestId
  useEffect(() => {
    if (!requestId) {
      setErrorInfo({
        title: 'Invalid Link',
        message: 'This report link appears to be invalid.',
      });
      setPageState('error');
    }
  }, [requestId]);

  // Auto-redirect to analysis if on generation tab but no generation data
  useEffect(() => {
    if (pageState === 'ready' && currentTab === 'generation' && !hasGeneration) {
      // Redirect to analysis tab since generation data is not available
      handleTabChange('analysis');
    }
  }, [pageState, currentTab, hasGeneration, handleTabChange]);

  // Page title based on state
  const pageTitle = useMemo(() => {
    if (pageState === 'ready' && metadata?.userName) {
      return `${metadata.userName}'s Hair Report | Match Medic`;
    }
    return 'View Report | Match Medic';
  }, [pageState, metadata]);

  // Render based on page state
  if (pageState === 'password') {
    return (
      <>
        <Helmet>
          <title>View Report | Match Medic</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <PasswordWall
          onSubmit={handlePasswordSubmit}
          isLoading={isVerifying}
          error={passwordError}
        />
      </>
    );
  }

  if (pageState === 'loading') {
    return (
      <>
        <Helmet>
          <title>Loading Report | Match Medic</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <LoadingScreen />
      </>
    );
  }

  if (pageState === 'error') {
    return (
      <>
        <Helmet>
          <title>Error | Match Medic</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <ErrorScreen
          title={errorInfo.title}
          message={errorInfo.message}
          onRetry={handleRetry}
        />
      </>
    );
  }

  // Ready state - show report
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Content based on current tab */}
      {currentTab === 'analysis' ? (
        <AnalysisReportView
          analysis={analysis}
          metadata={metadata}
          originalImageUrl={images.originalImageUrl}
          onSwitchTab={() => handleTabChange('generation')}
          hasGeneration={hasGeneration}
          activeTab={currentTab}
          onTabChange={handleTabChange}
          viewReportId={viewReportState?.session?.id}
          reportRequestId={requestId}
          onContactButtonClick={handleContactButtonClick}
          onPhoneSubmission={handlePhoneSubmission}
          onEmailSubmission={handleEmailSubmission}
        />
      ) : (
        <GenerationReportView
          originalImageUrl={images.originalImageUrl}
          generatedImageUrl={images.generatedImageUrl}
          metadata={metadata}
          candidateScorePercent={candidateScorePercent}
          expectedSatisfactionPercent={expectedSatisfactionPercent}
          onSwitchTab={() => handleTabChange('analysis')}
          hasAnalysis={hasAnalysis}
          activeTab={currentTab}
          onTabChange={handleTabChange}
          viewReportId={viewReportState?.session?.id}
          reportRequestId={requestId}
          onContactButtonClick={handleContactButtonClick}
          onPhoneSubmission={handlePhoneSubmission}
          onEmailSubmission={handleEmailSubmission}
        />
      )}
    </>
  );
}
